import { Trans, useTranslation } from 'next-i18next'

import { Product } from '@/types'
import prices from '@/lib/prices'
import { Label } from '@/components/text'

export type AccountType = 'essentials' | 'growth' | 'business'

export type BillingType = 'none' | 'monthly' | 'annual'

const isPaymentPlan = (product?: string): product is Product =>
  !!product &&
  [
    'essentials-plan-monthly',
    'growth-plan-monthly',
    'growth-plan-monthly-coupon-wbrothers',
    'growth-plan-annual',
    'business-plan-monthly',
    'business-plan-annual',
    'business-plan-special'
  ].indexOf(product) >= 0

export const getPaymentPlan = (
  accountType: AccountType,
  frequency: BillingType
): Product => {
  const value = `${accountType}-plan-${frequency}`
  if (!isPaymentPlan(value)) return 'essentials-plan-monthly'
  return value
}

const isAccountType = (account?: string): account is AccountType | 'special' =>
  !!account &&
  ['essentials', 'growth', 'business', 'special'].indexOf(account) >= 0

const isBillingType = (billing?: string): billing is BillingType =>
  !!billing && ['none', 'monthly', 'annual'].indexOf(billing) >= 0

export const parsePlan = (
  product?: Product
): [AccountType | 'special', BillingType] => {
  if (!product) return ['essentials', 'none']
  // eslint-disable-next-line @typescript-eslint/no-unused-vars
  const [plan, _, billing] = product.split('-')
  if (product === 'business-plan-special') return ['special', 'none']
  if (!isAccountType(plan) || !isBillingType(billing))
    return ['essentials', 'none']
  return [plan, billing]
}

export type EnabledKey = boolean | React.ReactNode | undefined

type PlanTypes = {
  essentials?: EnabledKey
  growth?: EnabledKey
  business?: EnabledKey
}

export type PlanName = keyof PlanTypes

export type OldPlanName = 'personal' | 'family' | 'business'

export type Feature = PlanTypes & {
  key: string
  name: React.ReactNode
  tooltip?: React.ReactNode
  status?: React.ReactNode
}

export type FeatureGroup = {
  key: string
  title: string
  features: Feature[]
}

type Features = FeatureGroup[]

export type IncludedFeatures = {
  heading?: React.ReactNode
  included: {
    name: string
    tooltipHeading?: React.ReactNode
    tooltipContent: React.ReactNode
    showPoweredBy?: boolean
  }[]
}

export type Price = {
  annual: string
  monthly?: string
}

export type Plan = {
  name: React.ReactNode
  buttonText: React.ReactNode
  monthText: React.ReactNode
  description: React.ReactNode
  value: PlanName
  price: Price
  recommended?: boolean
  features: IncludedFeatures
  id?: string
}

export type Plans = Plan[]

export type PricingPlans = Record<PlanName, Plan>

export const planList: PlanName[] = ['essentials', 'growth', 'business']

export const isValidPlan = (value?: string): value is PlanName =>
  ['essentials', 'growth', 'business'].indexOf(value ?? '') >= 0

export const getNewPlanKey = (value: OldPlanName): PlanName => {
  if (value === 'personal') return 'essentials'
  if (value === 'family') return 'growth'
  return value
}

export const getRank = (value: PlanName): number => {
  if (value === 'essentials') return 1
  if (value === 'growth') return 2
  return 3
}

// Same plan is considered a downgrade since going yearly to monthly,
// takes effect after the yearly terms is over
export const isDowngrade = (current: PlanName, target: PlanName): boolean => {
  const currentRank = getRank(current)
  const targetRank = getRank(target)
  return currentRank >= targetRank
}

export const useFeatures = (): Features => {
  const { t } = useTranslation('pricing')
  const data: Features = [
    {
      key: 'rental-listing',
      title: t('pricing:Rental Listing Marketing'),
      features: [
        {
          key: 'listings',
          name: t('pricing:Unlimited listings'),
          tooltip: t(
            'pricing:Post one, or one thousand listings – all for free.'
          ),
          essentials: true,
          growth: true,
          business: true
        },
        {
          key: 'inquiries',
          name: t('pricing:Unlimited inquiries'),
          tooltip: t(
            'pricing:Receive and respond to tenant inquiries with instant chat.'
          ),
          essentials: true,
          growth: true,
          business: true
        },
        {
          key: 'multi-plat',
          name: t('pricing:Multi-platform listing sharing'),
          tooltip: t(
            'pricing:Share your listing to Facebook Marketplace and Craigslist with just a few clicks.'
          ),
          essentials: true,
          growth: true,
          business: true
        },
        {
          key: 'sharable',
          name: t('pricing:Shareable listing link'),
          tooltip: t(
            'pricing:Share your unique listing link anywhere and receive inquiries & applications through the liv.rent platform.'
          ),
          essentials: true,
          growth: true,
          business: true
        },
        {
          key: 'seo',
          name: t('pricing:SEO optimization'),
          tooltip: t(
            'pricing:Each listing is keyword and page optimized using current SEO best practices to reach renters who are searching for listings just like yours.'
          ),
          essentials: true,
          growth: true,
          business: true
        },
        {
          key: 'company-page',
          name: t('pricing:Company landing page'),
          tooltip: t(
            'pricing:A customized landing page that displays your company’s branding and highlights your listings portfolio.'
          ),
          growth: t('pricing:Standard'),
          business: t('pricing:Custom')
        },
        {
          key: 'automated-ad',
          name: t('pricing:Multi-platform advertising'),
          tooltip: t(
            'pricing:Your listing will be shared to a variety of other rental platforms like Facebook and Craigslist, and our team will manage all inquiries.'
          ),
          essentials: t('pricing:Pay per use'),
          growth: t('pricing:2 per month'),
          business: t('pricing:6 per month')
        }
      ]
    },
    {
      key: 'management',
      title: t('pricing:Management Tool'),
      features: [
        {
          key: 'standard-digital-contract',
          name: t('pricing:Standard digital contracts'),
          tooltip: t(
            'pricing:Government-standard digital leases vetted by our team of lawyers. (Currently available in B.C. & Ontario)'
          ),
          essentials: true,
          growth: true,
          business: true
        },
        {
          key: 'digital-sign',
          name: t('pricing:Digital contract signing'),
          tooltip: t(
            'pricing:Send & sign digital contracts with ease and keep track of documents with secure cloud storage.'
          ),
          essentials: true,
          growth: true,
          business: true
        },
        {
          key: 'contract-verification',
          name: t('pricing:Unique contract ID and verification'),
          tooltip: t(
            'pricing:A unique contract ID is generated upon signing a lease agreement and can be verified at verify.liv.rent for added security.'
          ),
          essentials: true,
          growth: true,
          business: true
        },
        {
          key: 'addendums',
          name: t('pricing:Addendums package'),
          tooltip: t(
            'pricing:A variety of lease addendums to add onto your lease for everything from smoking rules to subletting to pet policies.'
          ),
          growth: true,
          business: true
        },
        {
          key: 'screening',
          name: t(
            'pricing:Tenant screening report powered by Equifax® credit checks'
          ),
          tooltip: t(
            'pricing:The Trust Score gives you an all-encompassing score powered by Equifax® credit checks, employment/income documents, court records & more.'
          ),
          essentials: t('pricing:Pay per use'),
          growth: t('pricing:2 per month'),
          business: t('pricing:15 per month')
        }
      ]
    },
    {
      key: 'rent-collection',
      title: t('pricing:Rent Collection'),
      features: [
        {
          key: 'payouts',
          name: t(
            'pricing:Easy rental payout for Credit Card, UnionPay & Bitcoin'
          ),

          tooltip: t(
            "pricing:Renters can pay with multiple convenient options including by credit card, UnionPay & Bitcoin – you'll receive payouts via direct deposit."
          ),
          essentials: true,
          growth: true,
          business: true
        },
        {
          key: 'rent-reminders',
          name: t('pricing:Rent payment reminders'),
          tooltip: t(
            'pricing:Scheduled reminders help you stay on top of your rent collection and ensure you never miss a payment.'
          ),
          essentials: true,
          growth: true,
          business: true
        },
        {
          key: 'expense-statements',
          name: t('pricing:Income expense statements'),
          tooltip: t(
            'pricing:Keep track of your rental income and expenses with detailed statements stored securely in the cloud.'
          ),
          growth: true,
          business: true
        },
        {
          key: 'direct-deposits',
          name: t('pricing:Automated direct deposits (PAD)'),
          tooltip: t(
            'pricing:PAD integration allows you to set up and automatically receive rent payments straight to your account.'
          ),
          essentials: t('pricing:Pay per use'),
          growth: t('pricing:Limit of 25 listings'),
          business: t('pricing:Unlimited'),
          status: t('pricing:Coming Soon')
        }
      ]
    },
    {
      key: 'users',
      title: t('pricing:Users'),
      features: [
        {
          key: 'quantity',
          name: t('pricing:Quantity'),
          tooltip: t('pricing:Number of users you can add into your account'),
          essentials: t('pricing:Just you'),
          growth: t('pricing:Up to 3'),
          business: t('pricing:Up to 10')
        },
        {
          key: 'user-management',
          name: t('pricing:User Management'),
          tooltip: t(
            'pricing:Organize your team and assign specific listings.'
          ),
          growth: true,
          business: true
        }
      ]
    },
    {
      key: 'support-service',
      title: t('pricing:Support/Services'),
      features: [
        {
          key: 'custom-contract',
          name: t('pricing:Custom contracts'),
          tooltip: t(
            'pricing:Upload your company’s contract and have it integrated with our applications for easy lease creation & signing.'
          ),
          business: true
        },
        {
          key: 'custom-addendums',
          name: t('pricing:Custom addendums'),
          tooltip: t(
            'pricing:Upload your company’s addendums as a standard to add onto all your leases.'
          ),
          business: true
        },
        {
          key: 'priority-support',
          name: t('pricing:Priority support'),
          tooltip: t(
            'pricing:Our support team is always ready to help you optimize your rental process and unlock the power of the liv.rent platform.'
          ),
          business: true
        }
      ]
    }
  ]
  return data
}

export const usePricingPlans = (): PricingPlans => {
  const { t } = useTranslation('pricing')
  const essentialsFeatures = {
    heading: t('pricing:Includes'),
    included: [
      {
        name: t('pricing:Unlimited listings'),
        tooltipContent: t(
          'pricing:Post one, or one thousand listings - all for free.'
        )
      },
      {
        name: t('pricing:Chat with secure document storage'),
        tooltipContent: t(
          'pricing:Streamline applications with all your inquiries and documents in one place.'
        )
      },
      {
        name: t('pricing:Standard digital contracts'),
        tooltipContent: t(
          'pricing:Government-standard digital leases vetted by our team of lawyers. (Currently available in B.C., Alberta, & Ontario)'
        )
      },
      {
        name: t(
          'pricing:Rent collection for credit card, UnionPay, and Bitcoin'
        ),
        tooltipHeading: t('pricing:Rent Collection'),
        tooltipContent: t(
          "pricing:Renters can pay with multiple convenient options including credit card, UnionPay & Bitcoin – you'll receive payouts via direct deposit."
        )
      },
      {
        name: t('pricing:1 user seat'),
        tooltipContent: t(
          'pricing:Only a single user can access this account and listings.'
        )
      }
    ]
  }

  const growthFeatures = {
    heading: t('pricing:Everything in Essentials, plus'),
    included: [
      {
        name: t('pricing:2 Tenant screening reports per month'),
        tooltipHeading: t('pricing:Tenant screening reports'),
        tooltipContent: (
          <Trans
            i18nKey="pricing:trust score unlock tooltip content"
            values={{ price: prices.trustScore.unlock }}
            defaults="The Trust Score tenant screening report provides an in-depth analysis of applicants' reliability based on credit information from Equifax®, income & employment verification documents, court records, and more. Additional reports are available to purchase for ${{ price }} each."
          />
        ),
        showPoweredBy: true
      },
      {
        name: t('pricing:2 Multi-platform advertising per month'),
        tooltipHeading: t('pricing:Multi-platform advertising'),
        tooltipContent: t(
          'pricing:Your listing will be shared to a variety of other rental platforms like Facebook and Craigslist, and our team will manage all inquiries.'
        )
      },
      {
        name: t(
          'pricing:Standard digital contracts with lease protection addendums'
        ),
        tooltipContent: t(
          'pricing:Government-standard digital leases and protection addendums vetted by our team of lawyers. (Currently available in B.C., Alberta, & Ontario)'
        )
      },
      {
        name: t('pricing:Team management up to 3 users'),
        tooltipContent: t(
          'pricing:Keep your team organized and assign specific listings to individual users'
        )
      },
      {
        name: t('pricing:Unlimited Auto-reply messages'),
        tooltipContent: null
      }
    ]
  }

  const businessFeatures = {
    heading: t('pricing:Everything in Growth, plus'),
    included: [
      {
        name: t('pricing:15 Tenant screening reports per month'),
        tooltipHeading: t('pricing:Tenant screening reports'),
        tooltipContent: (
          <Trans
            i18nKey="pricing:trust score unlock tooltip content"
            values={{ price: prices.trustScore.unlock }}
            defaults="The Trust Score tenant screening report provides an in-depth analysis of applicants' reliability based on credit information from Equifax®, income & employment verification documents, court records, and more. Additional reports are available to purchase for ${{ price }} each."
          />
        ),
        showPoweredBy: true
      },
      {
        name: t('pricing:6 Multi-platform advertising per month'),
        tooltipHeading: t('pricing:Multi-platform advertising'),
        tooltipContent: t(
          'pricing:Your listing will be shared to a variety of other rental platforms like Facebook and Craigslist, and our team will manage all inquiries.'
        )
      },
      {
        name: t('pricing:Custom contracts and addendums'),
        tooltipContent: t(
          'pricing:Upload and integrate your company’s contracts and addendums for easy signing and storage directly on the liv.rent platform.'
        )
      },
      {
        name: t('pricing:Monthly analytics report (Add-on)'),
        tooltipContent: t(
          'pricing:Gain access to key insights such as the number of views, clicks and inquiries for up to 100 syndicated listings.'
        )
      },
      {
        name: t('pricing:Priority support'),
        tooltipContent: t(
          'pricing:Our support team is always on hand to help you optimize your rental process and unlock the power of the liv.rent platform.'
        )
      },
      {
        name: t('pricing:Team management up to 10 users'),
        tooltipContent: t(
          'pricing:Keep your team organized and assign specific listings to individual users'
        )
      }
    ]
  }

  const plans: PricingPlans = {
    essentials: {
      name: t('pricing:Essentials'),
      value: 'essentials',
      price: {
        annual: prices.plans.essentials.annual
      },
      description: t(
        'pricing:For starting landlords looking for basic rental management solutions.'
      ),
      features: essentialsFeatures,
      buttonText: t('pricing:Get Started'),
      monthText: t('pricing:forever'),
      id: 'landlord-pricing-essentials-signupnow-click'
    },
    growth: {
      name: t('pricing:Growth'),
      value: 'growth',
      price: {
        annual: prices.plans.growth.annual,
        monthly: prices.plans.growth.monthly
      },
      description: t(
        'pricing:For growing landlords and property managers who want to scale their portfolios.'
      ),
      features: growthFeatures,
      buttonText: t('pricing:Get Started'),
      monthText: (
        <Trans
          i18nKey="pricing:per month billed annually $ billed monthly"
          defaults="per month billed annually<0></0><1>${{ monthlyPrice }}</1> billed monthly"
          values={{ monthlyPrice: prices.plans.growth.monthly }}
          components={[
            <br key="pricing break growth" />,
            <Label key="pricing bold growth" type="large" as="span"></Label>
          ]}
        />
      ),
      id: 'landlord-pricing-growth-freetrial-click'
    },
    business: {
      name: t('pricing:Business'),
      value: 'business',
      price: {
        annual: prices.plans.business.annual,
        monthly: prices.plans.business.monthly
      },
      description: t(
        'pricing:For seasoned landlords and brokerages to maximize marketing and rental operations.'
      ),
      features: businessFeatures,
      buttonText: t('pricing:Get Started'),
      monthText: (
        <Trans
          i18nKey="pricing:per month billed annually $ billed monthly"
          defaults="per month billed annually<0></0><1>${{ monthlyPrice }}</1> billed monthly"
          values={{ monthlyPrice: prices.plans.business.monthly }}
          components={[
            <br key="pricing break business" />,
            <Label key="pricing bold business" type="large" as="span"></Label>
          ]}
        />
      ),
      id: 'landlord-pricing-business-freetrial-click'
    }
  }

  return plans
}
